import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Calendar, MapPin, Camera, Film } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Seo } from '../components/Seo';
import { publicApi, getImageUrl } from '../lib/api';
import { usePageContent } from '../hooks/usePageContent';

const OurWork = () => {
  const [events, setEvents] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = usePageContent('our_work');

  const activeCategory = searchParams.get('category') || 'all';

  useEffect(() => {
    const load = async () => {
      try {
        const [data, catData] = await Promise.all([
          publicApi.getEvents(),
          publicApi.getCategories().catch(() => []),
        ]);
        setEvents(data);
        setCategoryList(catData || []);
      } catch (error) {
        // Best-effort load; page renders its empty state on failure
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const CATEGORY_LABELS = Object.fromEntries(categoryList.map((c) => [c.slug, c.name]));

  const filteredEvents = (
    activeCategory === 'all'
      ? events
      : events.filter((e) => (e.categories || []).includes(activeCategory))
  ).slice().sort((a, b) => (b.featured === true) - (a.featured === true) || (a.order || 0) - (b.order || 0));

  const setCategory = (cat) => {
    const next = new URLSearchParams(searchParams);
    if (cat === 'all') next.delete('category');
    else next.set('category', cat);
    setSearchParams(next);
  };

  const categories = ['all', ...categoryList.map((c) => c.slug)];

  return (
    <div className="min-h-screen bg-[#1F1A17] pt-20">
      <Seo
        exactTitle="Our Work — Portfolio | Mayden Films"
        description="Explore Mayden Films' portfolio: event photography, cinematic videography, Glam Booth celebrations, portraits and self-directed creative projects across Melbourne and Cranbourne."
        path="/portfolio"
      />

      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-[#1F1A17] via-[#1A1612] to-[#1F1A17]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-[#EADBC8] mb-6">
            {t('hero_title_line1', 'Recent')} <span className="text-[#C5A880]">{t('hero_title_line2', 'Events')}</span>
          </h1>
          <p className="text-xl text-[#EADBC8]/70 max-w-3xl mx-auto mb-8">
            {t('hero_subtitle', 'A selection of celebrations we’ve had the privilege to be part of across Southeast Melbourne.')}
            <br className="hidden md:block" />
            <span className="text-[#C5A880] text-base">
              {t('hero_subtitle_note', 'Images are shared with permission.')}
            </span>
          </p>
          <div className="flex flex-nowrap sm:flex-wrap justify-start sm:justify-center gap-2 mt-6 overflow-x-auto sm:overflow-visible pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                data-testid={`filter-${cat}`}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                  activeCategory === cat
                    ? 'bg-[#C5A880] text-[#1F1A17]'
                    : 'bg-[#2A2420] text-[#EADBC8]/70 hover:bg-[#C5A880]/20 hover:text-[#C5A880]'
                }`}
              >
                {cat === 'all' ? 'All' : (CATEGORY_LABELS[cat] || cat)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {loading ? (
        <div className="text-center py-20 text-[#EADBC8]/60">Loading…</div>
      ) : filteredEvents.length === 0 ? (
        <section className="py-20 bg-[#1A1612]">
          <div className="max-w-2xl mx-auto px-4">
            <Card className="bg-[#2A2420] border-[#C5A880]/20">
              <CardContent className="p-12 text-center">
                <p className="text-[#EADBC8]/60">No events in this category yet. Check back soon!</p>
              </CardContent>
            </Card>
          </div>
        </section>
      ) : (
        <section className="py-16 bg-[#1A1612]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredEvents.map((event) => {
                const videoCount = (event.videos || []).length;
                return (
                  <Link
                    key={event.id}
                    to={`/our-work/${event.slug}`}
                    data-testid={`event-card-${event.slug}`}
                    className="group"
                  >
                    <Card className="bg-[#2A2420] border-[#C5A880]/20 hover:border-[#C5A880] transition-all duration-300 hover:scale-[1.02] overflow-hidden h-full">
                      <div className="relative aspect-[4/3] overflow-hidden bg-[#1F1A17]">
                        {event.hero_image ? (
                          <img
                            src={getImageUrl(event.hero_image, 'thumb')}
                            alt={`${event.title}${event.event_type ? ` — ${event.event_type}` : ''}`}
                            loading="lazy"
                            draggable={false}
                            onContextMenu={(e) => e.preventDefault()}
                            style={{ objectPosition: `${event.hero_focal_x ?? 50}% ${event.hero_focal_y ?? 50}%` }}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 select-none"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[#EADBC8]/30">No image</div>
                        )}
                        {event.featured && (
                          <div className="absolute top-4 right-4">
                            <span className="bg-[#1F1A17]/85 text-[#C5A880] border border-[#C5A880]/40 text-[10px] font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider">Featured</span>
                          </div>
                        )}
                        {event.event_type && (
                          <div className="absolute bottom-4 left-4">
                            <span className="bg-[#C5A880] text-[#1F1A17] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                              {event.event_type}
                            </span>
                          </div>
                        )}
                      </div>
                      <CardContent className="p-6">
                        <h3 className="text-[#EADBC8] text-xl font-bold mb-3 group-hover:text-[#C5A880] transition-colors">
                          {event.title}
                        </h3>
                        <div className="space-y-1 text-sm text-[#EADBC8]/60">
                          {event.event_date && (
                            <div className="flex items-center gap-2">
                              <Calendar size={14} className="text-[#C5A880]" />
                              <span>{event.event_date}</span>
                            </div>
                          )}
                          {event.location && (
                            <div className="flex items-center gap-2">
                              <MapPin size={14} className="text-[#C5A880]" />
                              <span>{event.location}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-[#C5A880] text-xs mt-4">
                          {event.photo_count > 0 && (
                            <span className="flex items-center gap-1.5"><Camera size={13} /> {event.photo_count} photos</span>
                          )}
                          {videoCount > 0 && (
                            <span className="flex items-center gap-1.5"><Film size={13} /> {videoCount} {videoCount === 1 ? 'video' : 'videos'}</span>
                          )}
                          <span className="ml-auto font-medium">View gallery →</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default OurWork;
